import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import { RadioButtonsGroup } from '@components/RadioButtonsGroup';
import { PhonePreviewFrame } from '@components/PhonePreviewFrame';
import { Typography } from '@components/Typography';
import { accountService, micrositesService } from '@services';
import { useTranslation } from '@hooks';
import { getCompanyAddress } from '@helpers/companies';
import { LOCALE } from '@constants/locale';
import { InfoOutlinedIcon } from 'assets/images/icons';

const Surface = {
    Microsite: 'microsite',
    Survey: 'survey',
    Coupon: 'coupon',
};

const EMPTY_REVIEWS = { count: 0, rating: 0 };
const EMPTY_ENRICHMENT = { locationId: null, reviews: EMPTY_REVIEWS, socialLinks: {} };

const SURVEY_ORIGIN = (() => {
    try {
        return new URL(import.meta.env.REACT_APP_SURVEY_DOMAIN).origin;
    } catch (_) {
        return '*';
    }
})();

const SurfaceTabs = styled(RadioButtonsGroup)({
    display: 'block',
    width: 'fit-content',
    maxWidth: '100%',
    margin: '0 auto 16px',
    '& .MuiFormControlLabel-root': {
        height: 32,
        flex: '0 0 auto',
        padding: '0 16px',
    },
});

const PreviewBrand = ({ locations }) => {
    const [surface, setSurface] = useState(Surface.Microsite);
    const [enrichment, setEnrichment] = useState(EMPTY_ENRICHMENT);

    const locale = useSelector(state => state.account?.account?.locale ?? LOCALE.INTERNATIONAL);

    const { watch } = useFormContext();
    const { t } = useTranslation();
    const iframeRef = useRef(null);

    const formLocations = watch('locations');
    const backgroundStyle = watch('backgroundStyle');
    const backgroundColor = watch('backgroundColor');
    const font = watch('font');
    const fontColor = watch('fontColor');
    const buttonStyle = watch('buttonStyle');
    const buttonBackgroundColor = watch('buttonBackgroundColor');
    const buttonFontColor = watch('buttonFontColor');
    const logo = watch('logo');

    const previewLocation = formLocations?.find(l => l.id?.length)?.id?.[0];
    const previewLocationId = previewLocation?.id ? +previewLocation.id : null;

    useEffect(() => {
        if (!previewLocationId) {
            setEnrichment(EMPTY_ENRICHMENT);
            return undefined;
        }
        let active = true;
        Promise.all([
            micrositesService.getLocationReviews(previewLocationId).then(
                data => ({ count: data?.count ?? 0, rating: data?.averageRating ?? 0 }),
                () => EMPTY_REVIEWS,
            ),
            accountService.getLocation(previewLocationId).then(
                detail => detail?.socialLinks || {},
                () => ({}),
            ),
        ]).then(([reviews, socialLinks]) => {
            if (active) {
                setEnrichment({ locationId: previewLocationId, reviews, socialLinks });
            }
        });
        return () => {
            active = false;
        };
    }, [previewLocationId]);

    const getPreviewData = () => {
        const selected = locations.find(l => l.companyId === previewLocationId) || {};
        const locationName = selected.locationName || selected.internalName || previewLocation?.label || '';
        const isCurrent = enrichment.locationId === previewLocationId;
        const reviews = isCurrent ? enrichment.reviews : EMPTY_REVIEWS;
        const socialLinks = isCurrent ? enrichment.socialLinks : {};

        return {
            surface,
            appearance: {
                backgroundStyle,
                backgroundColor,
                font,
                fontColor,
                buttonStyle,
                buttonBackgroundColor,
                buttonFontColor,
                logo,
            },
            location: {
                locationName,
                businessAddress: getCompanyAddress(selected.addressLine1, selected.city),
                hasBusinessAddress: !!selected.hasBusinessAddress,
                reviews,
                socialMediaLinks: {
                    facebook: socialLinks?.facebook,
                    instagram: socialLinks?.instagram,
                    youtube: socialLinks?.youTube,
                    x: socialLinks?.x,
                    linkedIn: socialLinks?.linkedIn,
                    tiktok: socialLinks?.tikTok,
                },
            },
            locale,
        };
    };

    const sendPreviewData = () => {
        const data = getPreviewData();
        iframeRef.current?.contentWindow?.postMessage({ type: 'pl-preview-brand', data }, SURVEY_ORIGIN);
    };

    const sendPreviewDataRef = useRef(sendPreviewData);
    sendPreviewDataRef.current = sendPreviewData;

    useEffect(() => {
        const controller = new AbortController();
        const handleMessage = event => {
            if (event.origin === SURVEY_ORIGIN && event.source === iframeRef.current?.contentWindow) {
                if (event.data.type === 'pl-preview-iframe-loaded') {
                    sendPreviewDataRef.current();
                }
            }
        };
        window.addEventListener('message', handleMessage, { signal: controller.signal });
        return () => controller.abort();
    }, []);

    useEffect(() => {
        sendPreviewData();
    }, [
        surface,
        backgroundStyle,
        backgroundColor,
        font,
        fontColor,
        buttonStyle,
        buttonBackgroundColor,
        buttonFontColor,
        logo,
        previewLocationId,
        locations,
        enrichment,
        locale,
    ]);

    return (
        <>
            <Typography
                variant="caption"
                sx={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '16px', textAlign: 'center' }}
            >
                <InfoOutlinedIcon style={{ transform: 'translateY(4px)', marginRight: '8px' }} />
                {t('SettingsBrands.previewDisclaimer')}
            </Typography>
            <SurfaceTabs
                value={surface}
                onChange={setSurface}
                options={[
                    { value: Surface.Microsite, label: t('common.microsite') },
                    { value: Surface.Survey, label: t('common.survey') },
                    { value: Surface.Coupon, label: t('common.coupon') },
                ]}
            />
            <PhonePreviewFrame>
                <iframe
                    ref={iframeRef}
                    allow="autoplay"
                    width="100%"
                    height="100%"
                    src={`${import.meta.env.REACT_APP_SURVEY_DOMAIN}/preview/brand`}
                    frameBorder="0"
                ></iframe>
            </PhonePreviewFrame>
        </>
    );
};

export default PreviewBrand;
